import { IconButton } from "@mui/material"
import dynamic from "next/dynamic"
import { useEffect, useMemo, useRef, useState } from "react"
import VideoPlayIcon from "reusable/icons/VideoPlayIcon"
import VideoStopIcon from "reusable/icons/VideoStopIcon"
import styled from "styled-components"

const VideoPlayer = dynamic(() => import("common/VideoPlayer"), {
  loading: () => <p>...</p>,
})

const StyledVideoWrapper = styled.div<{
  $height: string
  $maxHeight?: string
  $mobileHeight?: string
}>`
  max-height: ${(p) => p.$maxHeight || "100vh"};
  height: ${(p) => p.$mobileHeight || p.$height};
  ${(p) => p.theme.breakpoints.up("md")} {
    height: ${(p) => p.$height};
  }
  width: 100%;
  position: relative;
`

const StyledIconButton = styled(IconButton)<{
  $iconSize?: "small" | "large"
  $iconPosition?: "left" | "right"
}>`
  position: absolute;
  bottom: 0;
  padding: 0;
  width: 80px;
  margin-bottom: 90px;
  ${(p) =>
    p.$iconPosition === "left"
      ? p.$iconSize === "small"
        ? "margin-left: 40px; left: 0; margin-bottom: 40px;"
        : "margin-left: 10px; left: 0;"
      : "margin-right: 10px; right: 0;"}
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 40px;
    ${(p) =>
      p.$iconPosition === "left"
        ? "margin-left: 40px; left: 0;"
        : "margin-right: 40px; right: 0;"}
  }
  ${(p) =>
    p.$iconSize === "small" ? "width: 24px; height: 24px;" : "width: 80px;"}
`

type VideoModelFullScreenProps = {
  src: string
  imgDesktop: string
  imgMobile: string
  alt: string
  maxHeight?: string
  mobileHeight?: string
  iconSize?: "small" | "large"
  iconPosition?: "left" | "right"
}

const VideoModelFullScreen = ({
  src,
  imgDesktop,
  imgMobile,
  alt,
  maxHeight,
  mobileHeight,
  iconSize = "large",
  iconPosition,
}: VideoModelFullScreenProps) => {
  const [playing, setPlaying] = useState(false)
  const videoRef = useRef(null)

  const observer = useMemo(() => {
    if (typeof window === "undefined") return null

    return new IntersectionObserver(([entry]) =>
      setPlaying(entry.isIntersecting)
    )
  }, [])

  useEffect(() => {
    if (videoRef.current) {
      observer?.observe(videoRef.current)
    }

    return () => {
      observer?.disconnect()
    }
  }, [videoRef, observer])

  return (
    <StyledVideoWrapper
      $height={"90vh"}
      ref={videoRef}
      $maxHeight={maxHeight}
      $mobileHeight={mobileHeight}
    >
      <VideoPlayer
        url={src}
        light={false}
        label={alt}
        playingState={playing}
        loop={true}
        imgAlt={alt}
        imgUrlDesktop={imgDesktop}
        imgUrlMobile={imgMobile}
      />
      {playing ? (
        <StyledIconButton
          onClick={() => {
            setPlaying(false)
          }}
          aria-label="עצור סרטון"
          $iconSize={iconSize}
          $iconPosition={iconPosition}
        >
          <VideoStopIcon />
        </StyledIconButton>
      ) : (
        <StyledIconButton
          onClick={() => setPlaying(true)}
          aria-label="התחל סרטון"
          $iconSize={iconSize}
          $iconPosition={iconPosition}
        >
          <VideoPlayIcon />
        </StyledIconButton>
      )}
    </StyledVideoWrapper>
  )
}

export default VideoModelFullScreen
